import type { FrontendInstallContext } from "@p0security/integrations-installer";
import type { ZpaIntegration } from "@p0security/integrations-zpa-shared/types";
import { installedItems } from "@p0security/shared/install/installed";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

import type { ZpaInstaller } from "./types";

type ZpaInstructionParams = {
  /** Human-readable summary of what this integration grants P0 */
  accessDescription: string;
  /** Name used for both the ZPA role and the ZIdentity API client */
  integrationName: string;
  /**
   * Description of the permission selection for the role, rendered inline
   * after "with" in the role creation step.
   * E.g. "`Policy Management` with only Policies permission set to "Full" access"
   */
  permissionSelection: ReactNode;
  /**
   * Optional content rendered before Step 1. Use to provide prerequisite
   * information the user needs before starting, e.g. where to find account
   * credentials.
   */
  preamble?: ReactNode;
};

const zpaInstallInstructions =
  ({
    accessDescription,
    integrationName,
    permissionSelection,
  }: ZpaInstructionParams) =>
  (_context: FrontendInstallContext<ZpaIntegration>, _id: string) => ({
    help: (
      <>
        <Typography.Paragraph>{accessDescription}</Typography.Paragraph>
        <Typography.Paragraph strong>
          Step 1: Create a custom role in Zscaler Private Access
        </Typography.Paragraph>
        <Typography.Paragraph>
          In the ZPA admin console, navigate to{" "}
          <Typography.Text code>Configuration &amp; Control</Typography.Text>{" "}
          &rarr; <Typography.Text code>Administration Control</Typography.Text>{" "}
          &rarr; <Typography.Text code>Roles</Typography.Text>. Create a new
          role named <Typography.Text code>{integrationName}</Typography.Text>,
          with {permissionSelection}.
        </Typography.Paragraph>
        <Typography.Paragraph strong>
          Step 2: Create an API client in ZIdentity
        </Typography.Paragraph>
        <Typography.Paragraph>
          In the ZIdentity admin console, navigate to{" "}
          <Typography.Text code>Administration</Typography.Text> &rarr;{" "}
          <Typography.Text code>Integration</Typography.Text> &rarr;{" "}
          <Typography.Text code>API Clients</Typography.Text> and create a new
          client with the following settings:
        </Typography.Paragraph>
        <Typography.Paragraph>
          On the <Typography.Text code>Client</Typography.Text> tab:
          <ul>
            <li>
              Name: <Typography.Text code>{integrationName}</Typography.Text>
            </li>
            <li>
              Set <Typography.Text code>Access Token Validity</Typography.Text>{" "}
              to at least 5 minutes and{" "}
              <Typography.Text code>Status</Typography.Text> to enabled.
            </li>
            <li>
              Choose <Typography.Text code>Secret</Typography.Text> for client
              authentication, then click{" "}
              <Typography.Text code>Add</Typography.Text> to generate a secret.
              Save the secret value — you will need it on the next page.
            </li>
          </ul>
        </Typography.Paragraph>
        <Typography.Paragraph>
          On the <Typography.Text code>Resources</Typography.Text> tab:
          <ul>
            <li>
              Select <Typography.Text code>{integrationName}</Typography.Text>{" "}
              as a scope. If the role does not appear yet, wait a few minutes
              for it to propagate and refresh.
            </li>
          </ul>
        </Typography.Paragraph>
        <Typography.Paragraph>
          On the <Typography.Text code>Metadata</Typography.Text> tab, enter
          your details as the owner. Then click{" "}
          <Typography.Text code>Save</Typography.Text>.
        </Typography.Paragraph>
      </>
    ) as ReactNode,
  });

export const zpaInstaller: ZpaInstaller = {
  components: {
    "iam-write": {
      instructions: zpaInstallInstructions({
        accessDescription:
          "Complete the following steps to grant P0 full access to manage Zscaler Private Access policies.",
        integrationName: "P0 Security Access Management Integration",
        permissionSelection: (
          <>
            <Typography.Text code>Policy Management</Typography.Text> with only
            the Policies permission set to &ldquo;Full&rdquo; access
          </>
        ),
      }),
      optionProvider: async (context) => {
        const existingIamWrite = installedItems("iam-write", context.config);
        const networkAccessItems = installedItems(
          "network-access",
          context.config
        );
        return Object.entries(networkAccessItems)
          .filter(([id, item]) => !!item && !(id in existingIamWrite))
          .map(([id, item]) => ({ id, label: item?.label ?? id }));
      },
      items: {},
    },
    "network-access": {
      instructions: zpaInstallInstructions({
        accessDescription:
          "Complete the following steps to grant P0 read-only access to your Zscaler Private Access application segments.",
        integrationName: "P0 Security Network Access Integration",
        permissionSelection: (
          <>
            <Typography.Text code>Privileged Remote Access</Typography.Text>{" "}
            with all permissions set to &ldquo;Read Only&rdquo; access
          </>
        ),
      }),
      items: {},
    },
  },
};
